/* Maintenance rows come from the payments backend, exactly as the classic
   Maintenance page reads them (GET /api/premium/maintenance), and its two
   writes go to the same routes (2026-10-28): POST …/maintenance/vehicle for the
   service schedule and the paper dates, POST …/maintenance/compliance-ack for
   "not legal to run" acknowledged or undone. */
import { auth } from '../../firebase';

const BASE = (process.env.REACT_APP_PAYMENTS_URL || '').replace(/\/$/, '');

export interface MaintCompliance {
  kind: string; label: string; state: 'expired' | 'expiring';
  daysAgo?: number; daysLeft?: number; atMs?: number; acknowledged?: boolean;
}
export interface MaintRow {
  busId: string;
  docId: string | null;
  nickname: string | null;
  severity: 'high' | 'medium' | 'ok';
  config: {
    serviceIntervalKm: number | null; lastServiceOdometerKm: number | null;
    insuranceExpiryMs: number | null; inspectionExpiryMs: number | null; odometerKm: number | null;
  };
  service: {
    state: string; kmSinceService?: number; remainingKm?: number; intervalKm?: number;
    odometerKm?: number; daysRemaining?: number | null; overdueKm?: number; missing?: string[];
  };
  compliance: MaintCompliance[];
  /** Present only while the acknowledgement holds (engine/maintenance/complianceAck). */
  complianceAck?: { ackedAtMs: number; ackedByName: string | null; untilMs: number } | null;
  drift: { state: string; driftPct?: number; recentKmPerL?: number; baselineKmPerL?: number; extraLitres30d?: number | null; estMonthlyLossKES?: number | null };
}

export const maintenanceConfigured = () => !!BASE;

export async function fetchMaintenance(orgCode: string, refresh = false): Promise<MaintRow[]> {
  if (!BASE) throw new Error('Maintenance service is not configured');
  const token = await auth.currentUser?.getIdToken();
  if (!token) throw new Error('Sign in again to view maintenance');
  const res = await fetch(`${BASE}/api/premium/maintenance?orgCode=${encodeURIComponent(orgCode)}${refresh ? '&refresh=1' : ''}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = await res.json();
  if (!data.ok) throw new Error(data.message || 'Could not load maintenance');
  return Array.isArray(data.maintenance) ? data.maintenance : [];
}

/* Every date here is a Nairobi calendar date, whatever the browser's time zone:
   the classic page's rule (2026-09-14). Kenya keeps no daylight saving. */
const NAIROBI_OFFSET_MS = 3 * 60 * 60 * 1000;

/** ms → "yyyy-mm-dd" on the Nairobi calendar, as `<input type="date">` takes it. */
export const toInputDate = (ms: number | null | undefined) =>
  ms && Number.isFinite(ms) ? new Date(ms + NAIROBI_OFFSET_MS).toISOString().slice(0, 10) : '';

export const todayYmd = () => toInputDate(Date.now());

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** "14 Sep" on the Nairobi calendar. */
export const nairobiDay = (ms: number) => {
  const d = new Date(ms + NAIROBI_OFFSET_MS);
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
};

type Answer = { ok: true } | { ok: false; message: string };

async function post(path: string, body: object): Promise<Answer> {
  if (!BASE) return { ok: false, message: 'Maintenance isn’t available here yet.' };
  const token = await auth.currentUser?.getIdToken();
  if (!token) return { ok: false, message: 'Sign in again to save.' };
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  return data.ok ? { ok: true } : { ok: false, message: data.message || 'Couldn’t save. Try again in a minute.' };
}

/** The schedule and the dates. A field left out is left alone; null clears it. */
export const saveVehicle = (orgCode: string, docId: string, body: {
  serviceIntervalKm?: string | null; lastServiceOdometerKm?: string | null;
  insuranceExpiry?: string | null; inspectionExpiry?: string | null;
}) => post('/api/premium/maintenance/vehicle', { orgCode, docId, ...body });

/** "Not legal to run" acknowledged — reminders off for 30 days — or undone. */
export const setComplianceAck = (orgCode: string, docId: string, undo: boolean) =>
  post('/api/premium/maintenance/compliance-ack', { orgCode, docId, undo });
